import { defineStore } from "pinia";
import { RouteLocation } from "vue-router";
import { WEBSOCKET_READY_STATE } from "@supplier/meta";
import router from "@/routers";
import { useUserStore } from "@/stores/user";
import { useKeepAliveStore } from "@/stores/keepAlive";
import { MAX_TAG_NUMBER } from "@/config";
import { getUserInfo } from "@/utils/common";
import enterChangeDialog from "@/components/EnterChangeDialog/index";

/* tabsMenuProps */
export interface TabsMenuProps {
    icon?: string;
    title: string;
    path: string;
    name: string;
    close: boolean;
    isKeepAlive?: boolean;
}

/* TabsState */
export interface TabsState {
    nextReload: boolean;
    tabsMenuList: TabsMenuProps[];
}

let connected = false;
let failure = false;
let WS: WebSocket;
let timer: ReturnType<typeof setInterval> | undefined;

export const useTabsStore = defineStore({
    id: "zhongsheng-tabs",
    state: (): TabsState => ({
        nextReload: true,
        tabsMenuList: []
    }),
    actions: {
        // Add Tabs
        async addTabs(tabItem: TabsMenuProps) {
            if (this.tabsMenuList.every(item => item.path !== tabItem.path)) {
                if (this.tabsMenuList.some(item => item.name === tabItem.name)) {
                    const index = this.tabsMenuList.findIndex(item => item.name === tabItem.name);
                    this.tabsMenuList[index] = tabItem;
                } else {
                    this.tabsMenuList.push(tabItem);
                }
            }
            if (this.tabsMenuList.length > MAX_TAG_NUMBER) await this.removeLimitExceededTabs();
        },
        // Remove Tabs
        async removeTabs(tabPath: string, callback?: () => void, isCurrent: boolean = true, closeCurrent: boolean = false) {
            const tabsMenuList = this.tabsMenuList;
            if (isCurrent) {
                tabsMenuList.forEach((item, index) => {
                    if (item.path !== tabPath) return;
                    const nextTab = tabsMenuList[index + 1] || tabsMenuList[index - 1];
                    if (!nextTab) return;
                    if (!callback) router.push(nextTab.path);
                });
            }
            if (!callback && closeCurrent) await router.push(tabsMenuList[0].path);
            this.tabsMenuList = tabsMenuList.filter(item => item.path !== tabPath);
            if (callback) callback();
        },
        // 按名称删 tab 新
        async removeTabsByName(tabName: string, callback?: () => void, isCurrent: boolean = true, closeCurrent: boolean = false) {
            const tabsMenuList = this.tabsMenuList;
            if (isCurrent) {
                tabsMenuList.forEach((item, index) => {
                    if (item.name !== tabName) return;
                    const nextTab = tabsMenuList[index + 1] || tabsMenuList[index - 1];
                    if (!nextTab) return;
                    if (!callback) router.push(nextTab.path);
                });
            }
            if (!callback && closeCurrent) await router.push(tabsMenuList[0].path);
            this.tabsMenuList = tabsMenuList.filter(item => item.name !== tabName);
            if (callback) callback();
        },
        // 按照配置模块MAX_TAG_NUMBER删除超出数量的tab
        async removeLimitExceededTabs(currentPath?: string) {
            const maxTagNumber = MAX_TAG_NUMBER;
            if (!maxTagNumber) return;
            const tabsMenuList = this.tabsMenuList;
            this.tabsMenuList = tabsMenuList.slice(-maxTagNumber);
            const keepAliveStore = useKeepAliveStore();
            keepAliveStore.setKeepAliveName(this.tabsMenuList.map(item => item.name));
            if (currentPath && !this.tabsMenuList.every(item => item.path === currentPath))
                await router.push(this.tabsMenuList[this.tabsMenuList.length - 1].path);
        },
        // Close Tabs On Side
        async closeTabsOnSide(path: string, type: "left" | "right") {
            const currentIndex = this.tabsMenuList.findIndex(item => item.path === path);
            if (currentIndex !== -1) {
                this.tabsMenuList = this.tabsMenuList.filter((item, index) => {
                    const flag = type === "left" ? index >= currentIndex : index <= currentIndex;
                    return flag || !item.close;
                });
            }
            const keepAliveStore = useKeepAliveStore();
            keepAliveStore.setKeepAliveName(this.tabsMenuList.map(item => item.name));
        },
        // Close MultipleTab
        async closeMultipleTab(tabsMenuValue?: string) {
            this.tabsMenuList = this.tabsMenuList.filter(item => {
                return item.path === tabsMenuValue || !item.close || item.path === router.currentRoute.value.path;
            });
            const keepAliveStore = useKeepAliveStore();
            keepAliveStore.setKeepAliveName(this.tabsMenuList.map(item => item.name));
        },
        closeAllTab() {
            this.tabsMenuList = [];
            const keepAliveStore = useKeepAliveStore();
            keepAliveStore.setKeepAliveName([]);
        },
        // Set Tabs
        async setTabs(tabsMenuList: TabsMenuProps[]) {
            this.tabsMenuList = tabsMenuList;
        },
        async closeTab(route: RouteLocation, callback?: () => void) {
            await this.removeTabs(route.fullPath ?? route.path, callback);
            const keepAliveStore = useKeepAliveStore();
            keepAliveStore.removeKeepAliveName(route.name as string);
        },
        connectWebsocket() {
            const user = useUserStore();

            // 重连配置
            const reconnectConfig = {
                maxRetries: 5, // 最大重试次数
                retryInterval: 3000, // 重试间隔(ms)
                currentRetries: 0 // 当前重试次数
            };

            // 关闭ws
            const closeWs = () => {
                if (!user.token) {
                    WS?.close();
                    if (timer) clearInterval(timer);
                    this.closeAllTab();
                }
            };

            // 已连接  失败  未登录
            if (connected || failure || !user.token) {
                closeWs();
                return;
            }
            connected = true;
            const auth = `authorization=${user.token}`;

            console.log("WS地址---", `${import.meta.env.VITE_WS_URL}?${auth}`);

            // 重连函数
            const attemptReconnect = () => {
                if (reconnectConfig.currentRetries >= reconnectConfig.maxRetries) {
                    console.log("达到最大重试次数，停止重连");
                    failure = true;
                    return;
                }

                reconnectConfig.currentRetries++;
                console.log(`尝试第 ${reconnectConfig.currentRetries} 次重连...`);

                setTimeout(() => {
                    if (!user.token) {
                        console.log("用户token不存在，停止重连");
                        return;
                    }
                    this.connectWebsocket(); // 重新调用连接方法
                }, reconnectConfig.retryInterval);
            };

            try {
                if (timer) clearInterval(timer);
                WS = new WebSocket(`${import.meta.env.VITE_WS_URL}?${auth}`);

                WS.onopen = () => {
                    console.log("建立WS成功---", WS.readyState);
                    // 连接成功时重置重试计数
                    reconnectConfig.currentRetries = 0;
                    failure = false;
                };

                WS.onmessage = async (res: MessageEvent) => {
                    if (!res?.data) return;
                    const data = JSON.parse(res.data as string);
                    console.log("data1231231231", data);
                    debugger;
                    if (data?.type === 1) {
                        await getUserInfo();
                        this.closeAllTab();
                        // 只显示当前页面的tab
                        const currentRoute = router.currentRoute.value;
                        const tabsParams: TabsMenuProps = {
                            icon: currentRoute.meta.icon as string,
                            title: currentRoute.meta.title as string,
                            path: currentRoute.fullPath,
                            name: currentRoute.name as string,
                            close: !currentRoute.meta.isAffix
                        };
                        this.addTabs(tabsParams).then();
                        if (this.nextReload) {
                            // 强制刷新页面
                            window.location.reload();
                        } else {
                            this.setNextReload(true);
                        }
                    }
                    // 客户企业主体发生变更
                    if (data?.type === 4) {
                        await enterChangeDialog();
                    }
                };

                WS.onclose = (event: object) => {
                    console.log("WS关闭链接---", event);
                    connected = false;

                    // 连接关闭时尝试重连
                    if (user.token) {
                        // 只有用户已登录时才重连
                        attemptReconnect();
                    }
                };

                WS.onerror = (err: object) => {
                    console.log("WS连接错误---", err);
                    failure = true;
                    connected = false;

                    // 发生错误时尝试重连
                    if (user.token) {
                        attemptReconnect();
                    }
                };

                // 每25秒发送一个心跳信息
                const loopConnection = () => {
                    timer = setInterval(() => {
                        if (WS && WS.readyState === WEBSOCKET_READY_STATE.OPEN.code) {
                            try {
                                WS.send(JSON.stringify({ heartBeat: "HEARTBEAT" }));
                            } catch (error) {
                                console.log("心跳发送失败", error);
                                // 心跳发送失败也尝试重连
                                attemptReconnect();
                            }
                        } else {
                            connected = false;
                            // WebSocket状态不正常时尝试重连
                            attemptReconnect();
                        }
                    }, 25000);
                };

                loopConnection();
            } catch (err) {
                failure = true;
                connected = false;
                console.error("WS连接异常---", err);

                // 捕获异常时也尝试重连
                if (user.token) {
                    attemptReconnect();
                }
            }
        },

        setNextReload(nextReload: boolean) {
            this.nextReload = nextReload;
        }
    }
    // persist: piniaPersistConfig("zhongsheng-tabs")
});
